import * as debug from 'debug';
import { index } from 'fp-ts/lib/Array';
import { TypeOf, union, literal } from 'io-ts';
import { Path, Router } from 'sdi/router';
import { getNumber } from 'sdi/util';
import {
    clearEditStatus,
    clearTextForm,
    setLayout,
    setRenderGlobalInfo,
    setTheme,
} from './app';
import {
    clearSelectedIssues,
    loadLinkedIssues,
    loadOsmObject,
    loadPreviousOsmObject,
    selectIssue,
    clearIssueOsmUser,
    loadIssueGeometry,
    clearExternalIssue,
    loadIssueTypes,
    loadIssueListByTheme,
} from './qa';
import { fromNullable, some } from 'fp-ts/lib/Option';
import {
    getAutoCenter,
    getAutoZoom,
    getLayout,
    getTheme,
} from '../queries/app';
import { coordToOpenLayerCoord, selectRow } from './table';
import { findIssue, getIssueTypes, getOsmResourceId } from '../queries/qa';
import { centerAndZoomOn, centerOn } from './map';
import { Issue } from '../remote';
import { wgs84toLB72Coords } from '../queries/geom';
import { Coordinate } from 'ol/coordinate';

// import { fetchAllUsersEvents } from './table';

const DurationIO = union([literal('week'), literal('month'), literal('year')]);
export type Duration = TypeOf<typeof DurationIO>;

const logger = debug('sdi:route');

// tslint:disable-next-line: variable-name
const RouteIO = union([
    literal('index'),
    literal('mapAndTable'),
    literal('map'),
    literal('table'),
    literal('tableSelect'),
    literal('about'),
]);
export type Route = TypeOf<typeof RouteIO>;

export const { home, route, navigate } = Router<Route>('qa');

export type Layout = Route;

const baseRouteParser = (p: Path) =>
    some({
        theme: index(0, p).getOrElse(''),
        id: index(1, p).chain(getNumber).getOrElse(0),
    });

const loadExternalData = (issue: Issue) => loadIssueGeometry(issue.id);
//     {
//     getType(issue.issue_type).map(t =>
//         t.origin !== 'ROUTING'
//             ? loadIssueGeometry(issue.id)
//             : loadIssueGeometryCollection(issue.id)
//     );
//     getType(issue.issue_type).map(t =>
//         t.origin === 'EXTERNAL' ? loadExternalIssue(issue.id) : () => {}
//     );
// };

const initId = (id: number) => {
    clearTextForm();
    clearIssueOsmUser();
    // clearFilterCriticality();
    // setNoteEditStatus(false);
    clearEditStatus();

    clearExternalIssue();
    findIssue(id).map(issue => {
        //getUserFromIssue(issue).map(loadIssueOsmUser);
        getOsmResourceId(issue).map(i => {
            loadOsmObject(i);
            loadPreviousOsmObject(i);
        });
        if (getIssueTypes()._tag === 'None') {
            loadIssueTypes().then(_it => {
                loadExternalData(issue);
            });
        } else {
            loadExternalData(issue);
        }
        //loadMapillary(issue.id, getLatLonCoord(issue.point.coordinates));
        getAutoZoom()
            ? centerAndZoomOn(
                  coordToOpenLayerCoord(
                      wgs84toLB72Coords(issue.point) as Coordinate
                  )
              )
            : () => {};
        getAutoCenter()
            ? centerOn(
                  coordToOpenLayerCoord(
                      wgs84toLB72Coords(issue.point) as Coordinate
                  )
              )
            : () => {};
    });
    selectIssue(id);
    selectRow(id);
    loadLinkedIssues(id);
};

// Route handlers
home('index', () => {
    clearSelectedIssues();
    setRenderGlobalInfo(true);
    setLayout('index');
});

route(
    'mapAndTable',
    route => {
        route.map(({ theme, id }) => {
            setTheme(theme);
            loadIssueListByTheme(theme);
            initId(id);
        });
        setLayout('mapAndTable');
    },
    baseRouteParser
);

route(
    'table',
    route => {
        route.map(({ theme, id }) => {
            setTheme(theme);
            loadIssueListByTheme(theme);
            initId(id);
        });
        setLayout('table');
    },
    baseRouteParser
);

route(
    'map',
    route => {
        route.map(({ theme, id }) => {
            setTheme(theme);
            loadIssueListByTheme(theme);
            initId(id);
        });
        setLayout('map');
    },
    baseRouteParser
);

route(
    'tableSelect',
    route => {
        route.map(({ theme, id }) => {
            setTheme(theme);
            loadIssueListByTheme(theme);
            initId(id);
        });
        setLayout('tableSelect');
    },
    baseRouteParser
);

route('about', () => {
    clearSelectedIssues();
    setLayout('about');
});

// end of route handlers
const routeParam = (path: string, id?: number) =>
    fromNullable(id).fold([path], id => [path, `${id}`]);

/**
 * Initial routing, should be called on loop effects
 * @param initial
 */
export const loadRoute = (initial: string[]) =>
    index(0, initial).map(prefix =>
        RouteIO.decode(prefix).map(c => navigate(c, initial.slice(1)))
    );

export const navigateIndex = () => navigate('index', []);
export const navigateAbout = () => navigate('about', []);
export const navigateMapAndTable = (theme: string, id?: number) =>
    navigate('mapAndTable', routeParam(theme, id));
export const navigateMap = (theme: string, id?: number) =>
    navigate('map', routeParam(theme, id));
export const navigateTable = (theme: string, id?: number) =>
    navigate('table', routeParam(theme, id));
export const navigateTableSelect = (theme: string, id: number) =>
    navigate('tableSelect', routeParam(theme, id));

export const navigateToSelection = (theme: string, id: number) => {
    initId(id);
    switch (getLayout()) {
        case 'index':
            return navigateMapAndTable(theme, id);
        case 'map':
            return navigateMap(theme, id);
        case 'table':
            return navigateTable(theme, id);
        default:
            return navigateMapAndTable(theme, id);
    }
};
export const navigateClear = () => {
    switch (getLayout()) {
        case 'index':
            return navigateIndex();
        case 'map':
            return navigateMap(getTheme().getOrElse(''));
        case 'table':
            return navigateTable(getTheme().getOrElse(''));
        default:
            return navigateMapAndTable(getTheme().getOrElse(''));
    }
};

logger('loaded');
